import React, { ReactElement, Ref, forwardRef, useEffect, useState, useContext } from "react"
import { Search, Toggle } from "@wfp/ui"
import Select, { SingleValue } from "react-select"
import { AutoSizer, Column, SortDirection, SortDirectionType, Table, TableCellProps } from "react-virtualized"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faTimes, faCheck } from "@fortawesome/free-solid-svg-icons"
import { useDebounce } from "react-use"

import { getGroupList, searchByString, sortList } from "utils/helpers"
import CustomOption from "components/Shared/Common/CustomOption"

import { FormField, OptionGroup, MappingField } from "interfaces/mappingInterfaces"
import { XslMappingContext } from "providers/XslMappingContext"

type Props = {
  fieldList?: FormField[]
  fieldName?: string
  mappedFields?: Record<string, MappingField>
  closePopover: () => void
  onAction: (field: FormField) => void
}

const Popover = forwardRef(
  (
    { fieldList = [], closePopover, onAction, mappedFields, fieldName }: Props,
    ref: Ref<HTMLDivElement>,
  ): ReactElement => {
    const { currentGroupFilterPopover, setCurrentGroupFilterPopover, inputSearchPopover, setInputSearchPopover } =
      useContext(XslMappingContext)

    const [isChecked, setIsChecked] = useState<boolean>(true)
    const [orderBy, setOrderBy] = useState<string>("fieldRow")
    const [orderDirection, setOrderDirection] = useState<SortDirectionType>(SortDirection.ASC)
    const [sortedList, setSortedList] = useState<FormField[]>(() =>
      sortList<FormField>({ fieldList, sortBy: "fieldRow", sortDirection: orderDirection }),
    )
    const [optionsGroup, setOptionsGroup] = useState<OptionGroup[]>([])

    const sort = ({ sortBy, sortDirection }: { sortBy: string; sortDirection: SortDirectionType }): void => {
      setOrderBy(sortBy)
      setOrderDirection(sortDirection)
      setSortedList(sortList({ fieldList: sortedList, sortBy, sortDirection }))
    }

    const handleSearch = (value: string): void => {
      let list = fieldList.filter((field: FormField) => {
        const isMapped = mappedFields?.[field.fieldName]
        return isChecked ? !isMapped : true
      })
      if (currentGroupFilterPopover)
        list = list?.filter(field => field.groupName?.includes(currentGroupFilterPopover?.label || ""))
      const filteredFields = searchByString(list ?? [], value, ["fieldName", "fieldLabel"])
      setSortedList(filteredFields)
    }

    useDebounce(
      () => {
        handleSearch(inputSearchPopover)
      },
      300,
      [inputSearchPopover, currentGroupFilterPopover],
    )

    useEffect(() => {
      setOptionsGroup(getGroupList(fieldList || [], "groupName"))
      handleSearch(inputSearchPopover)
    }, [fieldList?.length, isChecked])

    return (
      <div
        className="xls-popover z-50 fixed p-4 bg-white border rounded border-solid border-sky-600 h-max shadow-2xl w-[41%] right-2"
        ref={ref}
        style={{ top: window.scrollY <= 80 ? `8rem` : "1.5rem" }}
      >
        <div className="py-1">
          <h6>Map to: {fieldName}</h6>
        </div>
        <div
          onClick={closePopover}
          className="bg-white w-5 h-5 rounded-full absolute -top-3 -right-2 cursor-pointer flex align-center justify-center border border-solid border-sky-600"
        >
          <FontAwesomeIcon icon={faTimes} size="sm" color="var(--interactive-01)" />
        </div>
        <div className="flex align-center justify-between w-full">
          <Select
            id="popover-select"
            className="w-4/6 select-wrapper"
            classNamePrefix="select"
            placeholder="Select group"
            options={optionsGroup || []}
            getOptionLabel={option => option.label}
            getOptionValue={option => option.value}
            isSearchable
            components={{ Option: CustomOption }}
            value={currentGroupFilterPopover}
            onChange={value => setCurrentGroupFilterPopover(value as SingleValue<OptionGroup>)}
            isClearable
          />
          <div className="w-2/6 ml-4">
            <Search
              id="search-xls-co-form"
              onChange={value => setInputSearchPopover(value as unknown as string)}
              value={inputSearchPopover}
            />
          </div>
        </div>
        <div className="mt-1">
          <Toggle toggled={isChecked} onToggle={() => setIsChecked(prev => !prev)} labelB="Show unmapped" labelA="" />
        </div>

        <div className="mt-2" style={{ height: window.innerHeight - 350 + (window.scrollY <= 80 ? 0 : 120) }}>
          <AutoSizer>
            {({ width, height }) => {
              return (
                <Table
                  width={width}
                  height={height}
                  headerHeight={20}
                  rowHeight={30}
                  rowCount={sortedList?.length || 0}
                  rowGetter={({ index }) => (sortedList?.length ? sortedList[index] : null)}
                  sort={sort}
                  sortBy={orderBy}
                  sortDirection={orderDirection}
                  headerClassName="text-sm capitalize text-white"
                  rowClassName="border-solid border-b border-gray-200 cursor-pointer text-sm"
                  onRowClick={({ rowData }) => {
                    onAction(rowData)
                    closePopover()
                  }}
                >
                  <Column
                    label="Order"
                    dataKey="fieldRow"
                    width={100}
                    cellRenderer={(row: TableCellProps) => {
                      const isMapped = mappedFields?.[row.rowData.fieldName]
                      return isMapped ? (
                        <div className="d-flex flex-row justify-start align-center">
                          <FontAwesomeIcon icon={faCheck} size="sm" color="var(--support-02)" className="mr-1" />
                          {row.cellData}
                        </div>
                      ) : (
                        <div>
                          <span className="ml-2">{row.cellData}</span>
                        </div>
                      )
                    }}
                  />
                  <Column label="Name" dataKey="fieldName" width={170} />
                  <Column label="Description" dataKey="fieldLabel" disableSort width={300} flexGrow={1} />
                </Table>
              )
            }}
          </AutoSizer>
        </div>
      </div>
    )
  },
)

export default Popover
